import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, CalendarDays, Search, TrendingUp, TrendingDown } from "lucide-react";
import AdminLayout from "./AdminLayout";
import AdminUserLink from "../components/AdminUserLink";
import API from "../services/api";

function getIndiaDateValue() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date());
}

const money = (v) => {
  const n = Number(v || 0);
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${n}`;
};

const initials = (name = "") =>
  name.split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase() || "MK";

export default function AdminStakeAnalysis() {
  const [date, setDate] = useState(getIndiaDateValue);
  const [summary, setSummary] = useState({});
  const [markets, setMarkets] = useState([]);
  const [selected, setSelected] = useState(null); // { id, name }
  const [session, setSession] = useState("ALL");
  const [detail, setDetail] = useState(null);
  const [search, setSearch] = useState("");
  const [expandedKey, setExpandedKey] = useState("");

  const loadSummary = useCallback(async () => {
    try {
      const res = await API.get(`/admin/stake-analysis?date=${date}&market_type=normal`);
      setSummary(res.data?.summary || {});
      setMarkets(Array.isArray(res.data?.markets) ? res.data.markets : []);
    } catch {
      setSummary({});
      setMarkets([]);
    }
  }, [date]);

  const loadDetail = useCallback(async () => {
    if (!selected) return;
    try {
      const res = await API.get(
        `/admin/stake-analysis/market/${selected.id}?date=${date}&session=${session}`
      );
      setDetail(res.data || null);
    } catch {
      setDetail(null);
    }
  }, [selected, date, session]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { if (selected) loadDetail(); }, [loadDetail]);

  const goBack = () => {
    setSelected(null);
    setDetail(null);
    setSearch("");
    setExpandedKey("");
    setSession("ALL");
  };

  const selectMarket = (market) => {
    setSelected({ id: market.id, name: market.name });
    setDetail(null);
    setSearch("");
    setExpandedKey("");
    setSession("ALL");
  };

  const filteredGroups = useMemo(() => {
    const term = search.trim().toLowerCase();
    const groups = detail?.groups || [];
    if (!term) return groups;
    return groups.map((g) => ({
      ...g,
      numbers: g.numbers.filter((item) => String(item.number).toLowerCase().includes(term)),
    }));
  }, [detail, search]);

  const openResult = detail?.results?.open?.result || "---";
  const closeResult = detail?.results?.close?.result || "---";

  return (
    <AdminLayout>
      <section style={page}>
        {/* Header */}
        <div style={headerRow}>
          <div>
            <h1 style={titleStyle}>Market Stake Analysis</h1>
            <p style={subtitleStyle}>Open & Close session betting breakdown by number and user</p>
          </div>
          <div style={dateWrap}>
            <CalendarDays size={18} color="#2563eb" />
            <input
              type="date"
              value={date}
              onChange={(e) => { setDate(e.target.value); setSelected(null); setDetail(null); }}
              style={dateInput}
            />
          </div>
        </div>

        {/* Summary */}
        <div style={summaryGrid}>
          <SummaryCard label="Total Stake" value={money(summary.total_stake)} accent="#2563eb" />
          <SummaryCard label="Total Bets" value={Number(summary.total_bets || 0).toLocaleString("en-IN")} accent="#7c3aed" />
          <SummaryCard label="Markets" value={summary.total_markets || 0} accent="#0891b2" />
          <SummaryCard label="Open Stake" value={money(summary.open_stake)} accent="#059669" />
          <SummaryCard label="Close Stake" value={money(summary.close_stake)} accent="#ea580c" />
        </div>

        {/* Market list */}
        {!selected && (
          <div style={marketList}>
            {markets.length === 0 && (
              <div style={emptyState}>No markets found for {date}</div>
            )}
            {markets.map((m) => (
              <button
                key={m.id}
                type="button"
                style={marketCard}
                onClick={() => selectMarket(m)}
              >
                <div style={marketCardLeft}>
                  <span style={mkBadge}>{initials(m.name)}</span>
                  <div style={marketCardInfo}>
                    <strong style={marketName}>{m.name}</strong>
                    <span style={marketTime}>{m.open_time} – {m.close_time}</span>
                  </div>
                </div>
                <div style={marketCardStats}>
                  <div style={statChip("#ecfdf5", "#059669")}>
                    <TrendingUp size={13} />
                    <span>Open: <b>{money(m.open_stake)}</b></span>
                    <span style={{ color: "#94a3b8", fontSize: 11 }}>{m.open_bets} bets</span>
                  </div>
                  <div style={statChip("#fff7ed", "#ea580c")}>
                    <TrendingDown size={13} />
                    <span>Close: <b>{money(m.close_stake)}</b></span>
                    <span style={{ color: "#94a3b8", fontSize: 11 }}>{m.close_bets} bets</span>
                  </div>
                  <div style={totalChip}>
                    Total: <b>{money(m.total_stake)}</b>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Market detail */}
        {selected && (
          <>
            {/* Detail header */}
            <div style={detailHeader}>
              <button type="button" style={backBtn} onClick={goBack}>
                <ArrowLeft size={20} />
              </button>
              <span style={mkBadgeLg}>{initials(selected.name)}</span>
              <div style={{ flex: 1 }}>
                <strong style={{ fontSize: 17, fontWeight: 900 }}>{selected.name}</strong>
                <div style={{ fontSize: 13, opacity: 0.8, marginTop: 3 }}>{date.split("-").reverse().join("/")}</div>
              </div>

              {/* Session toggle */}
              <div style={sessionToggle}>
                {["ALL", "OPEN", "CLOSE"].map((s) => (
                  <button
                    key={s}
                    type="button"
                    style={s === session ? sessionBtnActive : sessionBtn}
                    onClick={() => setSession(s)}
                  >
                    {s}
                  </button>
                ))}
              </div>

              {/* Search */}
              <div style={searchBox}>
                <Search size={15} />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search number..."
                  style={searchInput}
                />
              </div>
            </div>

            {/* Result strip */}
            <div style={resultStrip}>
              <ResultCell label="OPEN Result" value={openResult} color="#059669" />
              <div style={stripDivider} />
              <ResultCell label="CLOSE Result" value={closeResult} color="#ea580c" />
              <div style={stripDivider} />
              <ResultCell label="Open Winners" value={detail?.results?.open?.winners || 0} color="#7c3aed" />
              <div style={stripDivider} />
              <ResultCell label="Close Winners" value={detail?.results?.close?.winners || 0} color="#7c3aed" />
              <div style={stripDivider} />
              <ResultCell label="Total Payout" value={money((detail?.results?.open?.total_payout || 0) + (detail?.results?.close?.total_payout || 0))} color="#2563eb" />
            </div>

            {!detail && <div style={emptyState}>Loading...</div>}

            {filteredGroups.map((group) => {
              const active = group.numbers.find((item) => `${group.key}-${item.number}` === expandedKey);
              const isOpen = group.key?.startsWith("OPEN");
              return (
                <section key={group.key} style={groupCard}>
                  <div style={groupTop}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ ...sessionPill, background: isOpen ? "#ecfdf5" : "#fff7ed", color: isOpen ? "#059669" : "#ea580c", border: `1px solid ${isOpen ? "#86efac" : "#fdba74"}` }}>
                        {isOpen ? "OPEN" : "CLOSE"}
                      </span>
                      <strong style={{ fontSize: 14, fontWeight: 800 }}>{group.label}</strong>
                    </div>
                    <div style={groupStats}>
                      <span>Stake: <b>{money(group.stake)}</b></span>
                      <span>Bets: <b>{group.bets}</b></span>
                      <span>Active: <b>{group.active}/{group.total_slots || "—"}</b></span>
                    </div>
                  </div>

                  <div style={numberGrid}>
                    {group.numbers.map((item, idx) => {
                      const key = `${group.key}-${item.number}`;
                      const isExpanded = key === expandedKey;
                      return (
                        <button
                          key={key}
                          type="button"
                          style={{
                            ...numberCell,
                            ...(idx === 0 ? goldCell : idx === 1 ? silverCell : idx === 2 ? bronzeCell : {}),
                            ...(isExpanded ? activeCell : {}),
                          }}
                          onClick={() => setExpandedKey(isExpanded ? "" : key)}
                        >
                          <span style={betsBubble}>{item.bets}</span>
                          <b style={{ fontSize: 14 }}>{item.number}</b>
                          <small style={{ fontSize: 11 }}>{money(item.stake)}</small>
                        </button>
                      );
                    })}
                    {group.numbers.length === 0 && <div style={emptyGroup}>No stake in this group</div>}
                  </div>

                  {active && (
                    <div style={userPanel}>
                      <div style={userPanelHead}>
                        <strong>Users on {active.number}</strong>
                        <span>{active.users?.length || 0} users · {money(active.stake)}</span>
                      </div>
                      <div style={userPanelBody}>
                        {(active.users || []).map((u) => (
                          <div key={`${group.key}-${active.number}-${u.user_id}`} style={userRow}>
                            <AdminUserLink userId={u.user_id} name={u.user_name || `User ${u.user_id}`} phone={u.user_phone || "-"} />
                            <span style={{ color: "#64748b", fontSize: 12 }}>{u.bets} bets</span>
                            <b style={{ color: "#0f172a" }}>{money(u.stake)}</b>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </section>
              );
            })}
          </>
        )}
      </section>
    </AdminLayout>
  );
}

function SummaryCard({ label, value, accent }) {
  return (
    <article style={{ ...summaryCard, borderTop: `3px solid ${accent}` }}>
      <b style={{ fontSize: 22, fontWeight: 900, color: accent }}>{value}</b>
      <span style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>{label}</span>
    </article>
  );
}

function ResultCell({ label, value, color }) {
  return (
    <div style={{ textAlign: "center", padding: "0 12px" }}>
      <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 3 }}>{label}</div>
      <b style={{ fontSize: 15, color }}>{value}</b>
    </div>
  );
}

const statChip = (bg, color) => ({
  display: "flex", alignItems: "center", gap: 5,
  background: bg, color, borderRadius: 8, padding: "4px 10px", fontSize: 12, fontWeight: 600,
});

const page = { display: "grid", gap: 18, maxWidth: "100%" };
const headerRow = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 };
const titleStyle = { margin: 0, fontSize: 22, fontWeight: 900, color: "#0f172a" };
const subtitleStyle = { margin: "5px 0 0", color: "#64748b", fontSize: 13 };
const dateWrap = { height: 44, display: "flex", alignItems: "center", gap: 10, border: "1px solid #cbd5e1", background: "#fff", padding: "0 14px", borderRadius: 10 };
const dateInput = { border: 0, outline: 0, fontWeight: 700, fontSize: 14, color: "#0f172a" };

const summaryGrid = { display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12 };
const summaryCard = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "16px 18px", display: "grid", gap: 6 };

const marketList = { display: "grid", gap: 10 };
const marketCard = {
  display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12,
  background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: "14px 18px",
  cursor: "pointer", textAlign: "left", transition: "box-shadow .15s",
};
const marketCardLeft = { display: "flex", alignItems: "center", gap: 12 };
const mkBadge = {
  width: 44, height: 44, borderRadius: 12, background: "linear-gradient(135deg,#2563eb,#1d4ed8)",
  color: "#fff", display: "grid", placeItems: "center", fontWeight: 900, fontSize: 14, flexShrink: 0,
};
const marketCardInfo = { display: "grid", gap: 3 };
const marketName = { fontSize: 15, fontWeight: 800, color: "#0f172a" };
const marketTime = { fontSize: 12, color: "#64748b" };
const marketCardStats = { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" };
const totalChip = {
  background: "#f1f5f9", color: "#334155", borderRadius: 8, padding: "4px 12px",
  fontSize: 12, fontWeight: 700,
};

const detailHeader = {
  minHeight: 76, background: "linear-gradient(90deg,#1e40af,#2563eb)", color: "#fff",
  borderRadius: 14, display: "flex", alignItems: "center", gap: 12, padding: "0 14px", flexWrap: "wrap",
};
const backBtn = {
  width: 40, height: 40, border: 0, background: "rgba(255,255,255,.15)", color: "#fff",
  display: "grid", placeItems: "center", cursor: "pointer", borderRadius: 10, flexShrink: 0,
};
const mkBadgeLg = {
  ...mkBadge, width: 48, height: 48, background: "rgba(255,255,255,.2)", fontSize: 16,
};
const sessionToggle = { display: "flex", gap: 4 };
const sessionBtn = {
  height: 34, padding: "0 14px", border: "1px solid rgba(255,255,255,.3)",
  background: "transparent", color: "rgba(255,255,255,.7)", cursor: "pointer", borderRadius: 8,
  fontSize: 12, fontWeight: 700,
};
const sessionBtnActive = {
  ...sessionBtn, background: "rgba(255,255,255,.25)", color: "#fff",
  border: "1px solid rgba(255,255,255,.5)",
};
const searchBox = {
  height: 40, minWidth: 180, background: "#fff", color: "#374151",
  display: "flex", alignItems: "center", gap: 8, padding: "0 12px", borderRadius: 10,
};
const searchInput = { border: 0, outline: 0, fontSize: 13, width: "100%", background: "transparent" };

const resultStrip = {
  background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12,
  minHeight: 60, display: "flex", alignItems: "center", justifyContent: "center",
  gap: 0, flexWrap: "wrap", padding: "8px 0",
};
const stripDivider = { width: 1, height: 32, background: "#e5e7eb" };

const groupCard = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, overflow: "hidden" };
const groupTop = {
  height: 52, display: "flex", alignItems: "center", justifyContent: "space-between",
  padding: "0 18px", borderBottom: "1px solid #f1f5f9", flexWrap: "wrap", gap: 8,
};
const sessionPill = { padding: "2px 10px", borderRadius: 99, fontSize: 11, fontWeight: 800 };
const groupStats = { display: "flex", gap: 18, color: "#64748b", fontSize: 12, fontWeight: 600 };

const numberGrid = { display: "grid", gridTemplateColumns: "repeat(10, 1fr)", gap: 6, padding: 14 };
const numberCell = {
  minHeight: 72, border: "1px solid #e2e8f0", background: "#fafafa",
  position: "relative", display: "grid", placeItems: "center", alignContent: "center",
  gap: 4, cursor: "pointer", color: "#0f172a", borderRadius: 10,
};
const activeCell = { outline: "2px solid #2563eb", borderColor: "#2563eb", transform: "translateY(-1px)", background: "#eff6ff" };
const goldCell = { background: "linear-gradient(180deg,#fbbf24,#f59e0b)", color: "#fff", border: 0 };
const silverCell = { background: "linear-gradient(180deg,#9ca3af,#6b7280)", color: "#fff", border: 0 };
const bronzeCell = { background: "linear-gradient(180deg,#fb923c,#ef4444)", color: "#fff", border: 0 };
const betsBubble = {
  position: "absolute", top: 5, right: 5, background: "#dbeafe", color: "#2563eb",
  borderRadius: 99, minWidth: 18, height: 18, display: "grid", placeItems: "center",
  fontSize: 10, fontWeight: 800,
};
const emptyGroup = { gridColumn: "1 / -1", padding: 20, color: "#94a3b8", textAlign: "center", fontSize: 13 };

const userPanel = { margin: "0 14px 14px", border: "1px solid #bfdbfe", borderRadius: 12, background: "#eff6ff", overflow: "hidden" };
const userPanelHead = {
  minHeight: 44, padding: "0 14px", display: "flex", alignItems: "center",
  justifyContent: "space-between", color: "#1d4ed8", borderBottom: "1px solid #bfdbfe",
  fontWeight: 700, fontSize: 13, flexWrap: "wrap", gap: 8,
};
const userPanelBody = { display: "grid", gap: 6, padding: 10 };
const userRow = {
  display: "grid", gridTemplateColumns: "1fr auto auto", gap: 10, alignItems: "center",
  background: "#fff", border: "1px solid #dbeafe", borderRadius: 10, padding: "8px 12px",
};

const emptyState = { padding: 40, textAlign: "center", color: "#94a3b8", fontSize: 14 };
